import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { useT } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  AlertCircle, CheckCircle, Clock, Building2, Phone, Megaphone,
  Plus, ArrowRight, FileText
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * TenantDashboard
 * Tenants see ONLY data relevant to their own unit:
 *   • their own complaints (count + latest 5)
 *   • their assigned building(s)
 *   • notices for their building
 *   • emergency contacts
 *   • quick "Raise a new complaint" CTA
 * NO portfolio metrics, NO stock, NO PPM, NO certificates, NO staff/payroll.
 */
export default function TenantDashboard() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const t = useT();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [complaints, setComplaints] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [notices, setNotices] = useState([]);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/dashboard/stats`, { withCredentials: true }).catch(() => ({ data: {} })),
      axios.get(`${API}/complaints`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/buildings`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/notices`, { withCredentials: true }).catch(() => ({ data: [] })),
      axios.get(`${API}/contacts`, { withCredentials: true }).catch(() => ({ data: [] })),
    ]).then(([s, c, b, n, ct]) => {
      setStats(s.data || {});
      setComplaints(c.data || []);
      setBuildings(b.data || []);
      setNotices(n.data || []);
      setContacts(ct.data || []);
    }).catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, []);

  const statusBadge = (s) => {
    const map = {
      open: { cls: 'bg-amber-100 text-amber-800', label: 'Open' },
      assigned: { cls: 'bg-blue-100 text-blue-800', label: 'Assigned' },
      in_progress: { cls: 'bg-violet-100 text-violet-800', label: 'In Progress' },
      resolved: { cls: 'bg-emerald-100 text-emerald-800', label: 'Resolved' },
      closed: { cls: 'bg-slate-200 text-slate-700', label: 'Closed' },
    };
    const m = map[s] || map.open;
    return <Badge data-testid={`tenant-complaint-status-${s}`} className={`${m.cls} font-medium`}>{m.label}</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </Layout>
    );
  }

  const emergencyContacts = contacts.filter(c => c.is_emergency || c.category === 'emergency').slice(0, 4);
  const recentNotices = notices.slice(0, 4);
  const recentComplaints = complaints.slice(0, 5);

  const tiles = [
    { label: 'Open',          value: stats.open_complaints ?? 0,        icon: AlertCircle, bg: 'bg-amber-50',  iconBg: 'bg-amber-100',  iconColor: 'text-amber-600',  path: '/complaints?status=open' },
    { label: 'In Progress',   value: stats.in_progress_complaints ?? 0, icon: Clock,       bg: 'bg-violet-50', iconBg: 'bg-violet-100', iconColor: 'text-violet-600', path: '/complaints?status=in_progress' },
    { label: 'Resolved',      value: stats.resolved_complaints ?? 0,    icon: CheckCircle, bg: 'bg-emerald-50',iconBg: 'bg-emerald-100',iconColor: 'text-emerald-600',path: '/complaints?status=resolved' },
    { label: 'Total Tickets', value: stats.total_complaints ?? 0,       icon: FileText,    bg: 'bg-slate-50',  iconBg: 'bg-slate-100',  iconColor: 'text-slate-600',  path: '/complaints' },
  ];

  return (
    <Layout>
      <div data-testid="tenant-dashboard-page" className="space-y-8 fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-1">
              {t('dash.welcome', { name: user?.name || '' }) || `Welcome, ${user?.name || ''}`}
            </h1>
            <p className="text-base text-muted-foreground">Your complaints, notices and contacts in one place.</p>
          </div>
          <Button
            data-testid="tenant-new-complaint-btn"
            onClick={() => navigate('/complaints?new=1')}
            className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-md"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Raise New Complaint
          </Button>
        </div>

        {/* My building(s) */}
        {buildings.length > 0 && (
          <Card data-testid="tenant-my-building" className="p-5 border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
            <div className="flex items-start gap-3">
              <div className="p-3 rounded-xl bg-emerald-100">
                <Building2 className="w-6 h-6 text-emerald-700" />
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase font-semibold tracking-wide text-emerald-700">My Building</div>
                {buildings.map(b => (
                  <div key={b.id} className="mt-1">
                    <div className="text-lg font-bold text-slate-900">{b.name}</div>
                    {b.address && <div className="text-sm text-slate-600">{b.address}</div>}
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* My complaints summary tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {tiles.map((tile, i) => {
            const Icon = tile.icon;
            return (
              <Card
                key={i}
                data-testid={`tenant-tile-${tile.label.toLowerCase().replace(/ /g, '-')}`}
                role="button"
                tabIndex={0}
                onClick={() => navigate(tile.path)}
                className={`p-5 ${tile.bg} cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1`}
              >
                <div className={`p-2.5 rounded-lg ${tile.iconBg} inline-flex`}>
                  <Icon className={`w-5 h-5 ${tile.iconColor}`} />
                </div>
                <div className="mt-3 text-xs uppercase font-semibold tracking-wide text-slate-600">{tile.label}</div>
                <div className="text-3xl font-bold text-foreground mt-1">{tile.value}</div>
              </Card>
            );
          })}
        </div>

        {/* My recent complaints */}
        <Card data-testid="tenant-recent-complaints" className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-foreground">My Recent Complaints</h2>
              <p className="text-xs text-muted-foreground">Status, ticket number & last update</p>
            </div>
            <Button data-testid="tenant-view-all-complaints" variant="outline" size="sm" onClick={() => navigate('/complaints')}>
              View all <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
          {recentComplaints.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <AlertCircle className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p>You haven&apos;t raised any complaints yet.</p>
              <Button data-testid="tenant-raise-first" className="mt-3 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => navigate('/complaints?new=1')}>
                Raise your first complaint
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {recentComplaints.map(c => (
                <div
                  key={c.id}
                  data-testid={`tenant-complaint-${c.id}`}
                  className="py-3 flex items-center justify-between gap-3 cursor-pointer hover:bg-slate-50 rounded px-2"
                  onClick={() => navigate(`/complaints?id=${c.id}`)}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      {c.ticket_number && <span className="text-xs font-mono text-slate-500">{c.ticket_number}</span>}
                      <span className="font-medium text-slate-900 truncate">{c.title}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {c.category} · {new Date(c.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  {statusBadge(c.status)}
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Notices + Contacts */}
        <div className="grid lg:grid-cols-2 gap-6">
          <Card data-testid="tenant-notices" className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-amber-600" /> Notices
              </h2>
              <Button variant="outline" size="sm" onClick={() => navigate('/notices')}>
                View all <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
            {recentNotices.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No notices.</p>
            ) : (
              <div className="space-y-3">
                {recentNotices.map(n => (
                  <div key={n.id} data-testid={`tenant-notice-${n.id}`} className="p-3 rounded-lg bg-amber-50 border border-amber-100">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-semibold text-slate-900 text-sm truncate">{n.title}</span>
                      <Badge className={
                        n.severity === 'critical' ? 'bg-rose-100 text-rose-700' :
                        n.severity === 'warning' ? 'bg-amber-100 text-amber-700' :
                        'bg-sky-100 text-sky-700'
                      }>{n.severity}</Badge>
                    </div>
                    <p className="text-xs text-slate-600 line-clamp-2">{n.body}</p>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card data-testid="tenant-contacts" className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                <Phone className="w-5 h-5 text-rose-600" /> Emergency Contacts
              </h2>
              <Button variant="outline" size="sm" onClick={() => navigate('/contacts')}>
                View all <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
            {emergencyContacts.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No emergency contacts added yet.</p>
            ) : (
              <div className="space-y-2">
                {emergencyContacts.map(c => (
                  <div key={c.id} data-testid={`tenant-contact-${c.id}`} className="p-3 rounded-lg bg-rose-50 border border-rose-100 flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 text-sm truncate">{c.name}</div>
                      <div className="text-xs text-slate-500">{c.role_title || c.category}</div>
                    </div>
                    {c.phone && <a href={`tel:${c.phone}`} className="text-sm font-mono text-rose-700 hover:underline">{c.phone}</a>}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </Layout>
  );
}
